"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { FileCheck, Loader2, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";

const PARTNER_REVIEW_FILE = "PartnerReview.json";

const PLANNING_QUESTIONS = [
  {
    id: "PR-P1",
    text: "Are you as an engagement partner satisfied that sufficient and appropriate resources to perform the engagement have been assigned or made available to the engagement team in a timely manner, taking into account the nature and circumstances of the audit engagement, the member firm's policies or procedures, and any changes that may arise during the engagement?",
  },
  {
    id: "PR-P2",
    text: "Are you as an engagement partner satisfied that the members of the engagement team, and any auditor's external specialists who provide direct assistance, collectively have the appropriate competence and capabilities, including sufficient time to perform the audit engagement?",
  },
  {
    id: "PR-P3",
    text: "Have you as engagement partner, been involved in the risk assessment procedures on each significant account, consider the entity-level risk factors identified in:\ni. Client acceptance and continuance process\nii. Understanding of entity-level controls\niii. Interim review procedures, if applicable\niv. Knowledge obtained during past audits, including significant ongoing matters or changes in the entity or its environment\nv. Understanding other types of services that have been performed for the entity (eg. agreed upon procedures, permissible tax services, etc.)",
  },
  {
    id: "PR-P4",
    text: "Have you as an engagement partner been involved in the determination of how such risk factors influence the identification of financial statement level risks and the identification and assessment of risks at the assertion level",
  },
] as const;

type PlanningReviewState = Record<string, boolean>;

interface PartnerReviewPlanningProps {
  setActiveSection?: (section: string) => void;
  onSelect?: (section: string) => void;
}

export function PartnerReviewPlanning({ setActiveSection, onSelect }: PartnerReviewPlanningProps = {}) {
  const [confirmed, setConfirmed] = useState<PlanningReviewState>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saved" | "error">("idle");

  const loadResponses = useCallback(async () => {
    setLoading(true);
    try {
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const result = await (window as any).sharePointAPI.downloadClientJson(PARTNER_REVIEW_FILE);
        if (result?.success && result.data?.planningReview) {
          const pr = result.data.planningReview as Record<string, boolean>;
          const next: PlanningReviewState = {};
          PLANNING_QUESTIONS.forEach((q) => {
            next[q.id] = !!pr[q.id];
          });
          setConfirmed(next);
        } else {
          PLANNING_QUESTIONS.forEach((q) => {
            setConfirmed((prev) => ({ ...prev, [q.id]: false }));
          });
        }
      } else {
        PLANNING_QUESTIONS.forEach((q) => {
          setConfirmed((prev) => ({ ...prev, [q.id]: false }));
        });
      }
    } catch (error) {
      console.error("Failed to load PartnerReview.json", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadResponses();
  }, [loadResponses]);

  const saveResponses = useCallback(
    async (nextConfirmed: PlanningReviewState) => {
      setSaving(true);
      setSaveStatus("idle");
      try {
        let existingData: Record<string, unknown> = {};
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const res = await (window as any).sharePointAPI.downloadClientJson(PARTNER_REVIEW_FILE);
          if (res?.success && res.data && typeof res.data === "object") {
            existingData = res.data as Record<string, unknown>;
          }
        }

        const planningReview: Record<string, boolean> = {};
        PLANNING_QUESTIONS.forEach((q) => {
          planningReview[q.id] = !!nextConfirmed[q.id];
        });

        const payload = {
          ...existingData,
          planningReview,
        };

        if ((window as any).sharePointAPI?.uploadClientJson) {
          const uploadRes = await (window as any).sharePointAPI.uploadClientJson(
            PARTNER_REVIEW_FILE,
            payload
          );
          if (uploadRes?.success) {
            setSaveStatus("saved");
          } else {
            setSaveStatus("error");
          }
        } else {
          setSaveStatus("error");
        }
      } catch (error) {
        console.error("Failed to save PartnerReview.json", error);
        setSaveStatus("error");
      } finally {
        setSaving(false);
      }
    },
    []
  );

  const handleConfirmedChange = (questionId: string, checked: boolean) => {
    const next = { ...confirmed, [questionId]: checked };
    setConfirmed(next);
    saveResponses(next);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6 flex items-center justify-center">
        <div className="flex items-center gap-2 text-white/70">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading planning review...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Detailed Planning Review Header */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Detailed Planning Review</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const navigate = setActiveSection || onSelect;
                  navigate?.("detailed-planning-review-dashboard");
                }}
                className="border-white/20 bg-white/5 text-white hover:bg-white/10"
                data-allow-archival-edit={true}
              >
                View Dashboard
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500/30 to-blue-600/20 flex items-center justify-center">
                  <FileCheck className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Planning Review</CardTitle>
                  <p className="text-xs text-white/50 mt-0.5">Partner review of planning phase</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {saving && (
                  <span className="text-xs text-white/50 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Saving...
                  </span>
                )}
                {saveStatus === "saved" && !saving && (
                  <span className="text-xs text-green-400 flex items-center gap-1">Saved</span>
                )}
                {saveStatus === "error" && !saving && (
                  <span className="text-xs text-red-400">Save failed</span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {PLANNING_QUESTIONS.map((q, index) => (
              <div
                key={q.id}
                className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3"
              >
                <div className="flex gap-3">
                  <span className="text-sm font-medium text-white/60 shrink-0">{index + 1}.</span>
                  <p className="text-sm text-white/90 whitespace-pre-line flex-1">{q.text}</p>
                </div>
                <div className="flex items-center gap-2 pl-6">
                  <Checkbox
                    id={q.id}
                    checked={!!confirmed[q.id]}
                    onCheckedChange={(c) => handleConfirmedChange(q.id, c === true)}
                    className="border-white/20 data-[state=checked]:bg-[#4da3ff] data-[state=checked]:border-[#4da3ff]"
                  />
                  <Label
                    htmlFor={q.id}
                    className="text-sm text-white/80 cursor-pointer font-medium"
                  >
                    Confirmed
                  </Label>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
